import React, { useState, useEffect, useRef } from 'react';
import './Schedule.scss';
import '.././../../helpers/DateHelper';
import DateHelper from '.././../../helpers/DateHelper';

interface ScheduleItem {
  employeeId: string;
  scheduleId: string;
  stringSchedule_StartTime: string;
  stringSchedule_EndTime: string;
  stringSchedule_Hours: string;
  scheduleTimeRestrictionIsActive?: boolean;
  submissionHrWindow?: number;
}
      
interface ScheduleProps {
  schedules: ScheduleItem[];
  onScheduleSelect: (schedule: ScheduleItem) => void;
  selectedScheduleId: string;
  disabled?: boolean;
}

const Schedule: React.FC<ScheduleProps> = ({ schedules, onScheduleSelect, selectedScheduleId, disabled = false }) => {
  const [localSelectedId, setLocalSelectedId] = useState<string>('');
  const initializedRef = useRef(false);

  // Initialize only once (or when schedules first become available)
  useEffect(() => {
    if (!initializedRef.current && schedules.length > 0) {
      // Use parent's selectedScheduleId if already defined; otherwise default to first schedule.
      const initial =
        (selectedScheduleId && schedules.find(s => s.scheduleId === selectedScheduleId)) ||
        schedules[0];

      if (initial) {
        setLocalSelectedId(initial.scheduleId);
        // Only call parent if it hasn't already indicated a selection
        if (!selectedScheduleId) {
          onScheduleSelect(initial);
        }
      }
      initializedRef.current = true;
    }
  }, [schedules, selectedScheduleId, onScheduleSelect]);

  const handleSelection = (scheduleId: string) => {
    if (disabled || scheduleId === localSelectedId || scheduleId === selectedScheduleId) {
      return; // Avoid redundant state churn or disabled interaction
    }
    setLocalSelectedId(scheduleId);
    const found = schedules.find(s => s.scheduleId === scheduleId);
    if (found) {
      onScheduleSelect(found);
    }
  };

  // Sync local state whenever parent explicitly sets a selection (e.g., after submit reset)
  useEffect(() => {
    if (selectedScheduleId && selectedScheduleId !== localSelectedId) {
      setLocalSelectedId(selectedScheduleId);
    }
    // If parent clears selection (empty string), revert to first schedule if available
    if (!selectedScheduleId && schedules.length > 0 && localSelectedId !== schedules[0].scheduleId) {
      setLocalSelectedId(schedules[0].scheduleId);
      onScheduleSelect(schedules[0]);
    }
  }, [selectedScheduleId, schedules, localSelectedId, onScheduleSelect]);

  return (
    <div className="dropdown-container">
      <ul className="dropdown-schedule">
        {schedules.map(schedule => {
          const extractedTime = DateHelper.extractScheduleTime(schedule.stringSchedule_EndTime);
          const isSelected =
            schedule.scheduleId === selectedScheduleId ||
            schedule.scheduleId === localSelectedId;

          return (
            <li
              key={schedule.scheduleId}
              className={`dropdown-item-schedule ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => handleSelection(schedule.scheduleId)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              <span className="scheduleDate">
                {DateHelper.scheduleDateFormat(new Date(schedule.stringSchedule_StartTime))}
              </span>
              -
              <span className="scheduleTime">
                {' '}
                {extractedTime.hours}:{extractedTime.minutes} ({schedule.stringSchedule_Hours} Hours)
              </span>

              {isSelected && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="tick-mark bi bi-check-circle-fill"
                  viewBox="0 0 17 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                </svg>
              )}

              {/* {schedule.scheduleTimeRestrictionIsActive && schedule.submissionHrWindow && (
                <span className="restriction-badge">
                  Callout Restriction: {schedule.submissionHrWindow}h
                </span>
              )} */}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Schedule;
